import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of, Subject, throwError } from 'rxjs';
import { PageEndpoint } from '../endpoints/page';
import {
  IHome,
  IHomeTop10,
  ISearchSidebar,
  IAdversiting,
  IPagination,
  IRating,
  IHomeGeopointType,
  MarkerType,
} from '../models';
import { catchError, tap } from 'rxjs/operators';
import { STORAGE_KEYS_CONSTANTS } from '../constants/storage-keys';
import { StringUtil } from '@shared/utils';
import { Params } from '@angular/router';
import { Storage } from './storage';

/**
 * ### **PageService**
 *
 * Un Servicio en Angular es el mecanismo para compartir funcionalidad entre componentes,
 * en este caso se encarga de de compartir las funciones hacen diferentes peticiones o
 * llamados a distintos API's que genera el backend.Algunos componentes que hacen uso de
 * este servicio son:
 *  * {@link HomeComponent}
 *
 * * {@link PrivacypoliciesComponent}
 *
 * * {@link SearchComponent}
 */
@Injectable({
  providedIn: 'root',
})
export class Page {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(Storage);

  /**  Variable de tipo BehaviorSubject(observable) que maneja la estructura ISearchSidebar */
  sidebarDataSubject = new BehaviorSubject<ISearchSidebar | null>(null);

  registerCompany = new BehaviorSubject<{ name: string } | null>(null);

  /**
   * Private variable that stores the home information
   */
  private readonly privateHome = signal<IHome | null>(null);

  /** Public variable that exposes the home information as a read-only signal */
  readonly signalHome = this.privateHome.asReadonly();

  constructor() {
    this.home();
  }

  /**
   * Function that retrieves the landing page information and stores it in sessionStorage
   * to avoid multiple requests to the server.
   */
  private home(): void {
    if (this.homeInStorage()) {
      const homeData = this.storage.getSession(STORAGE_KEYS_CONSTANTS.HOME);
      this.privateHome.set(homeData || null);
    }

    const language = this.storage.getLocal(STORAGE_KEYS_CONSTANTS.LANGUAGE) || 'es';
    const headers = new HttpHeaders({
      'Accept-Language': language,
    });

    this.http
      .get<IHome>(PageEndpoint.landing, {
        headers,
        transferCache: false,
      })
      .pipe(
        tap((home) => {
          this.privateHome.set(home);
          this.storage.setSession(STORAGE_KEYS_CONSTANTS.HOME, JSON.stringify(home));
        }),
        catchError(() => throwError(() => new Error('Error fetching home data'))),
      )
      .subscribe();
  }

  /**
   * Función que obtiene la información de KEY_HOME_STORAGE
   */
  private homeInStorage(): boolean {
    return !!this.storage.getSession(STORAGE_KEYS_CONSTANTS.HOME);
  }

  /**
   * Función que hace una petición get a {@link PageEndpoint.privacyPolicies}
   * @returns Observable<any> Parametro que contiene la información de las politicas de privacidad.
   */
  privacyPolicy(): Observable<any> {
    return this.http.get<{ content: string }>(PageEndpoint.privacyPolicies);
  }

  /**
   * Función que hace una petición get a {@link PageEndpoint.termsCondition}
   * @returns Observable<any> Parametro que contiene la información de los terminos y condiciones.
   */
  termsCondition(): Observable<any> {
    return this.http.get<{ content: string }>(PageEndpoint.termsCondition);
  }

  /**
   * Función encargada de hacer una petición **get** a PageEndpoint.adversiting y obtiene la infromación de los avisos.
   * @param slug Parametro que hace referencia a un conjunto de avisos publicitarios.
   * @returns Observable<Array<IAdversiting>> Parametro que contiene los avisos publicitarios de la ruta especificada.
   */
  advertising(slug: string): Observable<Array<IAdversiting>> {
    return this.http.get<Array<IAdversiting>>(StringUtil.format(PageEndpoint.adversiting, slug));
  }

  /**
   * Función que hace una petición get a PageEndpoint.topten y obtiene la información del top 10 especificado
   */
  topTen(): Observable<Array<IHomeTop10>> {
    return this.http.get<Array<IHomeTop10>>(PageEndpoint.topten);
  }

  /**
   * Funcion encargada de hacer una peticion de tipo **get** y obtener la información del sidebar o barra lateral
   * de la landing de busquedas.
   */
  sidebarSearch(): Observable<ISearchSidebar> {
    return this.http
      .get<ISearchSidebar>(PageEndpoint.sidebarSearch)
      .pipe(tap((res) => this.sidebarDataSubject.next(res)));
  }

  /**
   * Función que permite realizar una petición de tipo **get** a una url especifica.
   * @param url Url de la petición
   * @returns Devuelve un observable con la respuesta de la petición.
   */
  getRequestUrl(url: string): Observable<any> {
    return this.http.get(url);
  }

  /**
   * Función que permite obtener las valoraciones o calificaciones hechas a los geomarcadores o ciudades
   * @param params Parametros que se envian en la petición
   * @returns Observable con la respuesta de la petición
   */
  getRatings(params: Params): Observable<IPagination<IRating[]>> {
    return this.http.get<IPagination<IRating[]>>(PageEndpoint.ratings, { params });
  }

  /**
   * Retrieves the list of geopoint types from the server.
   * @returns An Observable that emits an array of IHomeGeopointType objects representing the available geopoint types.
   */
  getGeopointsTypes(params: Params): Observable<IPagination<IHomeGeopointType[]>> {
    return this.http.get<IPagination<IHomeGeopointType[]>>(PageEndpoint.geopointTypes, { params });
  }

  /**
   * Retrieves the list of marker categories from the server.
   * @returns Observable with the response of the request
   */
  getMarkerCategories(params: Params): Observable<IPagination<MarkerType[]>> {
    return this.http.get<IPagination<MarkerType[]>>(PageEndpoint.markerCategories, { params });
  }
}
